import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { loginSchema, type LoginInput } from '@dongtan/shared';
import { useAuthStore } from '@/stores/authStore';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import SocialLoginButtons from '@/components/auth/SocialLoginButtons';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuthStore();
  const [serverError, setServerError] = useState('');

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginInput) => {
    setServerError('');
    try {
      await login(data.email, data.password);
      toast.success('로그인 되었습니다.');

      // 명시적 리다이렉트 경로가 없으면 역할별 대시보드로 이동
      if (from === '/') {
        const { user } = useAuthStore.getState();
        const role = user?.role;
        const dest =
          role === 'ADMIN' ? '/admin/dashboard' :
          role === 'AGENT' ? '/agents/dashboard' :
          '/dashboard';
        navigate(dest, { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message ||
        '로그인에 실패했습니다.';
      setServerError(message);
    }
  };

  return (
    <div>
      <h1 className="mb-2 text-2xl font-bold text-gray-900">로그인</h1>
      <p className="mb-8 text-sm text-gray-500">
        계정이 없으신가요?{' '}
        <Link to="/register" className="font-medium text-primary-600 hover:text-primary-700">
          회원가입
        </Link>
      </p>

      {serverError && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600">{serverError}</div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="이메일"
          type="email"
          placeholder="email@example.com"
          error={errors.email?.message}
          {...register('email')}
        />

        <Input
          label="비밀번호"
          type="password"
          placeholder="비밀번호를 입력하세요"
          error={errors.password?.message}
          {...register('password')}
        />

        <Button type="submit" className="w-full" size="lg" isLoading={isLoading}>
          로그인
        </Button>
      </form>

      <div className="mt-6 text-center">
        <Link to="/forgot-password" className="text-sm text-gray-500 hover:text-primary-600">
          비밀번호를 잊으셨나요?
        </Link>
      </div>

      <SocialLoginButtons />
    </div>
  );
}
